import { N_UrlResource } from "@specsheet-common/shared-types";
import { formatUrl } from "../url";

const IMPORTANT_INFO_ATTR_NAME = "Important info";

/** Bare www./http(s) URLs in attribute text (same idea as GFM autolinks). */
const URL_IN_TEXT_REGEX = /(?:https?:\/\/|www\.)[^\s<>"']+/gi;

type PlacementAttributeSource = {
  name: string;
  value: string;
};

export type PlacementDocumentsSource = {
  documents?: N_UrlResource[] | null;
  originalSpecs?: N_UrlResource[] | null;
  attributes?: PlacementAttributeSource[] | null;
};

const EMPTY_URL_RESOURCES: N_UrlResource[] = [];

const stripTrailingUrlPunctuation = (url: string): string =>
  url.replace(/[.,;:!?)\]}>]+$/g, "");

export const createUrlResource = (
  name: string | null,
  href: string
): N_UrlResource => ({
  name,
  data: { kind: "url", value: href },
  notes: null,
  fileSize: null,
});

/**
 * Last path segment → hyphens to spaces → capitalize first letter.
 * Expects a normalizable URL (www. or http(s)).
 * e.g. …/production-specifications → Production specifications
 */
export const formatDocumentNameFromUrlPath = (url: string): string => {
  try {
    const href = formatUrl(url);
    const parsed = new URL(href);
    const segments = parsed.pathname.split("/").filter(Boolean);
    const rawSegment = segments[segments.length - 1] ?? parsed.hostname;
    const withSpaces = decodeURIComponent(rawSegment).replace(/-/g, " ").trim();
    if (!withSpaces) {
      return href;
    }
    return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1);
  } catch {
    return url;
  }
};

/** First-wins dedupe by href. Returns the same array reference when length ≤ 1. */
export const dedupeUrlResourcesByHref = (
  resources: N_UrlResource[]
): N_UrlResource[] => {
  if (resources.length <= 1) {
    return resources;
  }

  const byUrl = new Map<string, N_UrlResource>();
  for (const resource of resources) {
    const url = resource.data?.value;
    if (!url || byUrl.has(url)) {
      continue;
    }
    byUrl.set(url, resource);
  }

  return byUrl.size === resources.length ? resources : [...byUrl.values()];
};

export const extractImportantInfoUrlResources = (
  attributes?: PlacementAttributeSource[] | null
): N_UrlResource[] => {
  if (!attributes?.length) {
    return EMPTY_URL_RESOURCES;
  }

  const importantInfo = attributes.find(
    (attr) => attr.name === IMPORTANT_INFO_ATTR_NAME
  )?.value;

  if (!importantInfo) {
    return EMPTY_URL_RESOURCES;
  }

  const matches = importantInfo.match(URL_IN_TEXT_REGEX);
  if (!matches?.length) {
    return EMPTY_URL_RESOURCES;
  }

  const resources: N_UrlResource[] = [];
  const seen = new Set<string>();

  for (const match of matches) {
    const href = formatUrl(stripTrailingUrlPunctuation(match));
    if (!href || seen.has(href)) {
      continue;
    }
    seen.add(href);
    resources.push(
      createUrlResource(formatDocumentNameFromUrlPath(href), href)
    );
  }

  return resources;
};

/**
 * Display-only Documents column resources:
 * documents → originalSpecs → Important info URLs.
 * Does not mutate stored placement data. Dedupes by URL.
 */
export const getDocumentsColumnResourcesForPlacement = (
  placement: PlacementDocumentsSource
): N_UrlResource[] => {
  const documents = placement.documents ?? EMPTY_URL_RESOURCES;
  const originalSpecs = placement.originalSpecs ?? EMPTY_URL_RESOURCES;
  const hasAttributes = Boolean(placement.attributes?.length);

  // Common case: only documents — return as-is (no alloc / no attribute scan).
  if (originalSpecs.length === 0 && !hasAttributes) {
    return documents;
  }

  const importantInfoUrls = hasAttributes
    ? extractImportantInfoUrlResources(placement.attributes)
    : EMPTY_URL_RESOURCES;

  if (originalSpecs.length === 0 && importantInfoUrls.length === 0) {
    return documents;
  }

  if (documents.length === 0 && importantInfoUrls.length === 0) {
    return originalSpecs;
  }

  if (documents.length === 0 && originalSpecs.length === 0) {
    return importantInfoUrls;
  }

  return dedupeUrlResourcesByHref([
    ...documents,
    ...originalSpecs,
    ...importantInfoUrls,
  ]);
};

/** Merge Documents column resources across placements in a booking row. */
export const getDocumentsColumnResourcesForPlacements = (
  placements: PlacementDocumentsSource[]
): N_UrlResource[] => {
  if (placements.length === 0) {
    return EMPTY_URL_RESOURCES;
  }
  if (placements.length === 1) {
    return getDocumentsColumnResourcesForPlacement(placements[0]);
  }
  return dedupeUrlResourcesByHref(
    placements.flatMap(getDocumentsColumnResourcesForPlacement)
  );
};
